import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Camera,
  Check,
  CheckCircle2,
  FileCheck2,
  FileText,
  ShieldCheck,
  Upload,
  UserRound,
  Wrench,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import AustralianPhoneInput, { isValidAustralianMobile } from '../../components/AustralianPhoneInput';
import LocationAutocomplete from '../../components/LocationAutocomplete';
import ServiceIcon from '../../components/ServiceIcon';
import { cn } from '../../components/ui';
import { useAuth } from '../../context/AuthContext';
import { servicesApi } from '../../api/servicesApi';
import { userApi } from '../../api/userApi';
import { verificationApi } from '../../api/verificationApi';
import { profileApi } from '../../api/profileApi';

const MAPS_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const STATES = ['QLD', 'NSW', 'VIC', 'SA', 'WA', 'TAS', 'NT', 'ACT'];
const DAYS = [
  ['Mon', 'Monday'], ['Tue', 'Tuesday'], ['Wed', 'Wednesday'], ['Thu', 'Thursday'],
  ['Fri', 'Friday'], ['Sat', 'Saturday'], ['Sun', 'Sunday'],
];
const STEPS = [
  { id: 1, label: 'Account', Icon: UserRound, required: true },
  { id: 2, label: 'Photo & verification', Icon: ShieldCheck, required: false },
  { id: 3, label: 'Services & rates', Icon: Wrench, required: true },
  { id: 4, label: 'Availability', Icon: CalendarDays, required: false },
];
const IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const DOC_TYPES = [...IMAGE_TYPES, 'application/pdf'];

const inputClass = 'box-border block w-full min-w-0   rounded-lg border-0 bg-[#06110c] px-3.5 py-3 text-sm font-medium text-white outline-none ring-1 ring-inset ring-white/10 transition placeholder:text-white/25 hover:ring-white/20 focus:ring-2 focus:ring-[#20c77a] disabled:cursor-not-allowed disabled:opacity-50';
const labelClass = 'mb-1.5 block text-[11px] font-semibold text-white/70';
const panelClass = 'rounded-xl border border-white/[0.09] bg-[#07140f]/80 p-4 sm:p-5';
const secondaryButton = 'inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.035] px-4 text-sm font-semibold text-white/75 transition hover:border-[#20c77a]/50 hover:bg-[#20c77a]/[0.08] hover:text-white disabled:opacity-50';
const primaryButton = 'inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-[#20c77a] bg-[#12ad68] px-5 text-sm font-bold text-[#02140b] shadow-[0_10px_30px_rgba(18,173,104,0.16)] transition hover:-translate-y-0.5 hover:bg-[#25d080] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0';

const ProviderRegister = () => {
  const navigate = useNavigate();
  const { register, loading } = useAuth();
  const [step, setStep] = useState(1);
  const [error, setError] = useState('');
  const [services, setServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [schedulePreset, setSchedulePreset] = useState('');
  const [form, setForm] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    address: '',
    suburb: '',
    state: 'QLD',
    postcode: '',
    lat: null,
    lng: null,
    radiusKm: 10,
    abn: '',
    selectedServices: [],
    rates: {},
    profileImage: null,
    idDocument: null,
    insuranceDocument: null,
    availableDays: [],
    startTime: '09:00',
    endTime: '17:00',
    emergencyAvailable: false,
    availabilityConfigured: false,
  });

  const patch = (next) => setForm((current) => ({ ...current, ...next }));

  useEffect(() => {
    let active = true;
    servicesApi.list()
      .then((data) => active && setServices((Array.isArray(data) ? data : []).filter((item) => item.status === 'Active')))
      .catch(() => active && setError('Services could not be loaded. Please refresh and try again.'))
      .finally(() => active && setServicesLoading(false));
    return () => { active = false; };
  }, []);

  useEffect(() => () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
  }, [imagePreview]);

  const handleLocationSelect = useCallback((place) => {
    setError('');
    setForm((current) => ({
      ...current,
      address: place.address || current.address,
      suburb: place.suburb || current.suburb,
      state: place.state || current.state,
      postcode: place.postcode || current.postcode,
      lat: place.lat,
      lng: place.lng,
    }));
  }, []);

  const handleLocationTyping = useCallback((value) => {
    setForm((current) => ({
      ...current,
      address: value,
      lat: null,
      lng: null,
    }));
  }, []);

  const accountValid = () => Boolean(
    form.fullName.trim() &&
    /^\S+@\S+\.\S+$/.test(form.email.trim()) &&
    isValidAustralianMobile(form.phone) &&
    form.password.length >= 8 &&
    form.password === form.confirmPassword &&
    form.address.trim() &&
    form.suburb.trim() &&
    form.state &&
    /^\d{4}$/.test(form.postcode.trim())
  );

  const serviceRateError = () => {
    if (!form.selectedServices.length) return 'Select at least one service you provide.';
    for (const id of form.selectedServices) {
      const service = services.find((item) => item._id === id);
      const rate = Number(form.rates[id]);
      if (!Number.isFinite(rate) || rate <= 0) return `Enter your hourly rate for ${service?.name || 'each selected service'}.`;
      const min = Number(service?.hourlyRateMin) || 0;
      const max = Number(service?.hourlyRateMax) || 0;
      if (min && rate < min) return `${service.name} must be at least $${min}/hr.`;
      if (max && rate > max) return `${service.name} cannot exceed $${max}/hr.`;
    }
    return '';
  };

  const validateStep = (target = step) => {
    if (target === 1) {
      if (!form.fullName.trim()) return 'Enter your full name.';
      if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) return 'Enter a valid email address.';
      if (!isValidAustralianMobile(form.phone)) return 'Enter a valid Australian mobile number.';
      if (form.password.length < 8) return 'Password must be at least 8 characters.';
      if (form.password !== form.confirmPassword) return 'Passwords do not match.';
      if (!form.address.trim()) return 'Enter your location.';
      if (!form.suburb.trim()) return 'Enter your suburb.';
      if (!form.state) return 'Select your state.';
      if (!/^\d{4}$/.test(form.postcode.trim())) return 'Enter a valid 4-digit Australian postcode.';
    }
    if (target === 3) return serviceRateError();
    return '';
  };

  const next = () => {
    const message = validateStep();
    setError(message);
    if (message) return;
    setStep((value) => Math.min(4, value + 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const back = () => {
    setError('');
    setStep((value) => Math.max(1, value - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleService = (service) => {
    const selected = form.selectedServices.includes(service._id);
    patch(selected
      ? { selectedServices: form.selectedServices.filter((id) => id !== service._id) }
      : {
          selectedServices: [...form.selectedServices, service._id],
          rates: { ...form.rates, [service._id]: form.rates[service._id] ?? service.hourlyRateMin ?? '' },
        });
  };

  const applyPreset = (preset) => {
    const choices = {
      weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'],
      weekends: ['Sat', 'Sun'],
      all: DAYS.map(([day]) => day),
      custom: form.availableDays,
    };
    setSchedulePreset(preset);
    patch({ availabilityConfigured: true, availableDays: choices[preset] || [] });
  };

  const toggleDay = (day) => {
    setSchedulePreset('custom');
    patch({
      availabilityConfigured: true,
      availableDays: form.availableDays.includes(day)
        ? form.availableDays.filter((value) => value !== day)
        : [...form.availableDays, day],
    });
  };

  const chooseProfileImage = (file) => {
    setError('');
    if (!file) return;
    if (!IMAGE_TYPES.includes(file.type)) return setError('Profile image must be JPG, PNG, WEBP or HEIC.');
    if (file.size > 5 * 1024 * 1024) return setError('Profile image must be 5MB or smaller.');
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    patch({ profileImage: file });
    setImagePreview(URL.createObjectURL(file));
  };

  const chooseDocument = (field, file) => {
    setError('');
    if (!file) return;
    if (!DOC_TYPES.includes(file.type)) return setError('Verification documents must be JPG, PNG, WEBP, HEIC or PDF.');
    if (file.size > 10 * 1024 * 1024) return setError('Verification documents must be 10MB or smaller.');
    patch({ [field]: file });
  };

  const uploadDocument = async (file, type) => {
    const uploaded = await verificationApi.uploadFile(file, { kind: type });
    if (!uploaded?.url) throw new Error(`${type} upload failed.`);
    await verificationApi.submitDocument({
      type,
      url: uploaded.url,
      originalName: file.name,
      mimeType: file.type,
    });
  };

  const submit = async () => {
    const accountError = validateStep(1);
    if (accountError) {
      setStep(1);
      setError(accountError);
      return;
    }
    const servicesError = validateStep(3);
    if (servicesError) {
      setStep(3);
      setError(servicesError);
      return;
    }
    if (form.availabilityConfigured && form.availableDays.length && form.startTime >= form.endTime) {
      setError('Choose a valid availability start and end time, or leave availability for later.');
      return;
    }

    setError('');
    setSubmitting(true);
    try {
      await register({
        name: form.fullName.trim(),
        email: form.email.trim(),
        phone: form.phone,
        password: form.password,
        role: 'provider',
        suburb: form.suburb.trim(),
        state: form.state,
        postcode: form.postcode.trim(),
        abn: form.abn.trim(),
      });

      const servicesPayload = form.selectedServices.map((id) => ({
        service: id,
        hourlyRateMin: Number(form.rates[id]),
        hourlyRateMax: Number(form.rates[id]),
      }));

      const serviceAreaPayload = {
        address: form.address.trim(),
        radiusKm: form.radiusKm,
        additionalAreas: [],
      };
      if (Number.isFinite(form.lat) && Number.isFinite(form.lng)) {
        serviceAreaPayload.lat = form.lat;
        serviceAreaPayload.lng = form.lng;
      }

      await Promise.all([
        userApi.updateServices({ services: servicesPayload }),
        userApi.updateProfile({
          location: {
            address: form.address.trim(),
            suburb: form.suburb.trim(),
            state: form.state,
            postcode: form.postcode.trim(),
          },
        }),
        userApi.updateServiceArea(serviceAreaPayload),
      ]);

      const optional = [];
      if (form.availabilityConfigured && form.availableDays.length) {
        optional.push(userApi.updateAvailability({
          timezone: 'Australia/Brisbane',
          exceptions: [],
          weekly: DAYS.map(([day]) => ({
            day,
            enabled: form.availableDays.includes(day),
            slots: form.availableDays.includes(day) ? [{ start: form.startTime, end: form.endTime }] : [],
          })),
        }));
      }
      if (form.emergencyAvailable) optional.push(userApi.completeOnboarding({ emergencyAvailable: true }));
      if (form.profileImage) {
        optional.push(
          verificationApi.uploadFile(form.profileImage, { kind: 'avatar' })
            .then((file) => file?.url && profileApi.setAvatar({ url: file.url }))
        );
      }
      if (form.idDocument) optional.push(uploadDocument(form.idDocument, 'id'));
      if (form.insuranceDocument) optional.push(uploadDocument(form.insuranceDocument, 'insurance'));
      if (optional.length) await Promise.allSettled(optional);

      navigate('/provider/dashboard', { replace: true });
    } catch (err) {
      setError(err?.message || 'Could not create your Gigger account. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const servicesReady = !serviceRateError();
  const verificationAdded = Boolean(form.profileImage || form.idDocument || form.insuranceDocument || form.abn.trim());
  const availabilityAdded = Boolean(form.availabilityConfigured && form.availableDays.length);

  return (
    <main className="min-h-screen overflow-x-hidden bg-[#020906] font-dm text-white">
      <title>Gigger Registration | GIGGS</title>
      <div className="grid min-h-screen w-full lg:grid-cols-[minmax(340px,0.78fr)_minmax(0,1.22fr)] xl:grid-cols-[minmax(400px,0.82fr)_minmax(0,1.18fr)]">
        <aside className="relative min-h-[230px] overflow-hidden bg-[#04110b] sm:min-h-[280px] lg:sticky lg:top-0 lg:min-h-screen lg:self-start">
          <video
            src="/gif/login-register.mp4"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            aria-label="Local Giggers offering services through Giggs"
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#04110b]/25 via-[#04110b]/20 to-[#020906]/90 lg:bg-gradient-to-t lg:from-[#020906]/95 lg:via-[#020906]/35 lg:to-[#04110b]/20" />
          <div className="relative z-10 flex min-h-[230px] flex-col justify-between p-5 sm:min-h-[280px] sm:p-7 lg:min-h-screen lg:p-10 xl:p-12">
            <Link to="/" className="inline-flex w-fit no-underline" aria-label="Giggs home">
              <img src="/images/logo-white.png" alt="Giggs" className="h-8 w-auto sm:h-9 lg:h-10" />
            </Link>
            <div className="max-w-lg lg:max-w-md">
              <span className="inline-flex rounded-full border border-white/10 bg-black/20 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#78efb4] backdrop-blur-sm sm:text-xs">Become a Gigger</span>
              <h1 className="mb-0 mt-3 max-w-md font-nunito text-[clamp(1.55rem,4vw,3.25rem)] font-bold leading-[1.08] tracking-[-0.025em] text-white lg:mt-5">Offer your skills. Work on your terms.</h1>
              <p className="mb-0 mt-2 hidden max-w-md text-sm leading-6 text-white/65 sm:block lg:mt-4 lg:text-base lg:leading-7">Create your Gigger profile, choose your services and rates, then set your availability whenever you are ready.</p>
            </div>
          </div>
        </aside>

        <section className="relative min-w-0 bg-[#020906] px-3 py-5 sm:px-5 sm:py-7 lg:px-7 lg:py-8 xl:px-10 2xl:px-14">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_70%_0%,rgba(25,177,105,0.11),transparent_28%),linear-gradient(rgba(255,255,255,0.014)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.014)_1px,transparent_1px)] bg-[size:auto,22px_22px,22px_22px]" />
          <div className="relative z-10 mx-auto w-full max-w-[980px]">
            <div className="mb-5 flex items-center justify-end sm:mb-7">
              <Link to="/login" className="text-xs font-medium text-white/50 no-underline transition hover:text-[#33df8d] sm:text-sm">Already have an account? <span className="font-semibold text-[#33df8d]">Sign in</span></Link>
            </div>

            <div className="mb-5 sm:mb-7">
              <span className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#4cdd98]">Step {step} of 4</span>
              <h2 className="mb-0 mt-1.5 font-nunito text-[clamp(1.65rem,4vw,2.35rem)] font-bold tracking-[-0.02em]">Gigger registration</h2>
            </div>

            <div className="overflow-hidden rounded-2xl border border-[#1c4b36]/70 bg-[#04100b]/95 shadow-[0_25px_90px_rgba(0,0,0,0.38)]">
              <StepProgress step={step} />
              <div className="p-4 sm:p-6 lg:p-7">
                {error && <div className="mb-5 rounded-lg border border-red-400/20 bg-red-500/10 px-4 py-3 text-sm font-medium text-red-200">{error}</div>}
                {step === 1 && <AccountStep form={form} patch={patch} onLocationSelect={handleLocationSelect} onLocationTyping={handleLocationTyping} />}
                {step === 2 && <VerificationStep form={form} patch={patch} imagePreview={imagePreview} chooseProfileImage={chooseProfileImage} chooseDocument={chooseDocument} />}
                {step === 3 && <ServicesStep services={services} loading={servicesLoading} form={form} patch={patch} toggleService={toggleService} />}
                {step === 4 && <AvailabilityStep form={form} patch={patch} schedulePreset={schedulePreset} applyPreset={applyPreset} toggleDay={toggleDay} accountReady={accountValid()} servicesReady={servicesReady} verificationAdded={verificationAdded} availabilityAdded={availabilityAdded} />}

                <div className="mt-6 flex flex-col-reverse gap-3 border-t border-white/[0.07] pt-5 sm:flex-row sm:items-center sm:justify-between">
                  <div className="flex items-center justify-between gap-3 sm:justify-start">
                    {step > 1
                      ? <button type="button" className={secondaryButton} onClick={back} disabled={submitting}><ArrowLeft size={16} /> Back</button>
                      : <Link to="/" className={`${secondaryButton} no-underline`}><ArrowLeft size={16} /> Home</Link>}
                    {(step === 2 || step === 4) && <span className="text-[10px] uppercase tracking-wider text-white/25">Optional step</span>}
                  </div>
                  {step < 4
                    ? <button type="button" className={primaryButton} onClick={next}>Continue <ArrowRight size={16} /></button>
                    : <button type="button" className={primaryButton} onClick={submit} disabled={submitting || loading}>{submitting || loading ? 'Creating account…' : 'Complete registration'} <CheckCircle2 size={16} /></button>}
                </div>
              </div>
            </div>
            <p className="mb-0 mt-4 text-center text-[10px] leading-4 text-white/25">By registering, you confirm that the information you provide is accurate and agree to Giggs' platform terms.</p>
          </div>
        </section>
      </div>
    </main>
  );
};

const StepProgress = ({ step }) => (
  <div className="border-b border-white/[0.07] px-3 py-4 sm:px-6 sm:py-5">
    <div className="grid grid-cols-4 gap-1 sm:gap-3">
      {STEPS.map(({ id, label, Icon, required }, index) => {
        const complete = step > id;
        const active = step === id;
        return (
          <div key={id} className="relative min-w-0 text-center">
            {index > 0 && <span className={cn('absolute right-1/2 top-4 h-px w-full', step >= id ? 'bg-[#20c77a]/70' : 'bg-white/10')} />}
            <span className={cn('relative z-10 mx-auto flex h-8 w-8 items-center justify-center rounded-full border text-[11px] font-bold sm:h-9 sm:w-9', complete || active ? 'border-[#20c77a] bg-[#0d6d43] text-[#7dffba]' : 'border-white/15 bg-[#06120d] text-white/35')}>
              {complete ? <Check size={14} strokeWidth={3} /> : <Icon size={14} />}
            </span>
            <span className={cn('relative z-10 mx-auto mt-2 block max-w-[130px] truncate px-1 text-[9px] font-semibold sm:text-[11px]', active ? 'text-[#4de498]' : complete ? 'text-white/65' : 'text-white/35')}>{label}</span>
            <span className="relative z-10 mt-0.5 hidden text-[8px] uppercase tracking-wider text-white/25 sm:block">{required ? 'Required' : 'Optional'}</span>
          </div>
        );
      })}
    </div>
  </div>
);

const Heading = ({ title, description, optional }) => (
  <div className="text-center">
    <div className="flex flex-wrap items-center justify-center gap-2">
      <h3 className="m-0 font-nunito text-xl font-bold tracking-[-0.015em] sm:text-2xl">{title}</h3>
      {optional && <span className="rounded-full border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white/35">Optional</span>}
    </div>
    <p className="mx-auto mb-0 mt-1.5 max-w-2xl text-xs leading-5 text-white/35 sm:text-sm">{description}</p>
  </div>
);

const Field = ({ label, required, hint, children }) => (
  <label className="block min-w-0">
    <span className={labelClass}>{label}{required && <span className="ml-1 text-[#35dc8b]">*</span>}</span>
    {children}
    {hint && <span className="mt-1.5 block text-[10px] leading-4 text-white/25">{hint}</span>}
  </label>
);

const AccountStep = ({ form, patch, onLocationSelect, onLocationTyping }) => (
  <div>
    <Heading title="Create your account" description="Enter your basic details. This step is required." />
    <div className="mt-5 grid gap-4 lg:grid-cols-2">
      <div className="space-y-4">
        <Field label="Full name" required><input className={inputClass} value={form.fullName} onChange={(e) => patch({ fullName: e.target.value })} placeholder="Enter your full name" autoComplete="name" /></Field>
        <Field label="Email" required><input className={inputClass} type="email" value={form.email} onChange={(e) => patch({ email: e.target.value })} placeholder="Enter your email" autoComplete="email" /></Field>
        <Field label="Phone number" required><AustralianPhoneInput className={inputClass} value={form.phone} onChange={(phone) => patch({ phone })} /></Field>
          <Field label="Password" required><input className={inputClass} type="password" value={form.password} onChange={(e) => patch({ password: e.target.value })} placeholder="Minimum 8 characters" autoComplete="new-password" /></Field>
      </div>

      <div className="space-y-4">
        <Field label="Location" required>
          <LocationAutocomplete
            apiKey={MAPS_KEY}
            value={form.address}
            onInputChange={onLocationTyping}
            onSelect={onLocationSelect}
            placeholder="Start typing your address or suburb…"
            className={inputClass}
            errorClassName="mt-2 text-xs font-medium text-red-300"
          />
        </Field>

        <div className="grid gap-3 sm:grid-cols-3">
          <Field label="Suburb" required>
            <input className={inputClass} value={form.suburb} onChange={(e) => patch({ suburb: e.target.value })} placeholder="Suburb" autoComplete="address-level2" />
          </Field>
          <Field label="State" required>
            <select className={`${inputClass} cursor-pointer`} value={form.state} onChange={(e) => patch({ state: e.target.value })}>
              {STATES.map((state) => <option className="bg-[#06110c]" key={state} value={state}>{state}</option>)}
            </select>
          </Field>
          <Field label="Postcode" required>
            <input className={inputClass} value={form.postcode} maxLength={4} inputMode="numeric" onChange={(e) => patch({ postcode: e.target.value.replace(/\D/g, '').slice(0, 4) })} placeholder="Postcode" autoComplete="postal-code" />
          </Field>
        </div>

        <Field label={`Service coverage radius: ${form.radiusKm} km`}>
          <div className="pt-1">
            <input
              type="range"
              min="1"
              max="100"
              step="1"
              value={form.radiusKm}
              onChange={(e) => patch({ radiusKm: Number(e.target.value) })}
              className="h-2 w-full cursor-ew-resize accent-[#20c77a] touch-pan-y"
              aria-label="Service coverage radius in kilometres"
            />
            <div className="mt-1.5 flex justify-between text-[9px] text-white/20"><span>1 km</span><span>100 km</span></div>
          </div>
        </Field>
          <Field label="Confirm password" required><input className={inputClass} type="password" value={form.confirmPassword} onChange={(e) => patch({ confirmPassword: e.target.value })} placeholder="Re-enter password" autoComplete="new-password" /></Field>

      </div>
    </div>
  </div>
);

const VerificationStep = ({ form, patch, imagePreview, chooseProfileImage, chooseDocument }) => (
  <div>
    <Heading title="Photo & verification" description="Profile photo, Government ID, insurance and ABN can all be added later." optional />
    <div className="mt-5 grid gap-5 lg:grid-cols-[0.8fr_1.2fr]">
      <div className={panelClass}>
        <h4 className="m-0 text-sm font-semibold">Profile photo</h4><p className="mb-0 mt-1 text-xs text-white/35">Use a clear photo of yourself.</p>
        <div className="mt-4 flex flex-col items-center">
          <div className="flex h-24 w-24 items-center justify-center overflow-hidden rounded-full border border-white/10 bg-[#06110c] text-white/25">{imagePreview ? <img src={imagePreview} alt="Profile preview" className="h-full w-full object-cover" /> : <UserRound size={40} strokeWidth={1.4} />}</div>
          <div className="mt-4 grid w-full gap-2 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
            <label className={`${primaryButton} cursor-pointer px-3`}><Camera size={16} /> Take a selfie<input type="file" accept="image/*" capture="user" className="hidden" onChange={(e) => chooseProfileImage(e.target.files?.[0])} /></label>
            <label className={`${secondaryButton} cursor-pointer px-3`}><Upload size={16} /> Upload photo<input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden" onChange={(e) => chooseProfileImage(e.target.files?.[0])} /></label>
          </div>
          {form.profileImage && <span className="mt-3 max-w-full truncate text-xs text-[#54dfa0]">{form.profileImage.name}</span>}
        </div>
      </div>
      <div className="space-y-3">
        <DocPicker icon={<FileText size={19} />} title="Government-issued ID" description="Driver licence, passport or proof-of-age card." file={form.idDocument} onChange={(file) => chooseDocument('idDocument', file)} />
        <DocPicker icon={<ShieldCheck size={19} />} title="Public Liability Insurance" description="Upload a current certificate of currency." file={form.insuranceDocument} onChange={(file) => chooseDocument('insuranceDocument', file)} />
        <Field label="ABN" hint="Optional — you can add this later too."><input className={inputClass} value={form.abn} onChange={(e) => patch({ abn: e.target.value })} placeholder="Enter your ABN" /></Field>
      </div>
    </div>
  </div>
);

const DocPicker = ({ icon, title, description, file, onChange }) => (
  <div className="flex min-w-0 flex-col gap-3 rounded-xl border border-white/[0.09] bg-[#07140f]/80 p-4 sm:flex-row sm:items-center">
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-white/[0.08] bg-[#06110c] text-[#3cd98f]">{file ? <FileCheck2 size={19} /> : icon}</span>
    <div className="min-w-0 flex-1"><h4 className="m-0 truncate text-xs font-semibold text-white/80 sm:text-sm">{title}</h4><p className="mb-0 mt-1 text-[10px] leading-4 text-white/30">{description}</p>{file && <p className="mb-0 mt-1 truncate text-[10px] text-[#54dfa0]">{file.name}</p>}</div>
    <label className={`${secondaryButton} shrink-0 cursor-pointer px-3 text-xs`}><Upload size={14} /> {file ? 'Replace' : 'Upload'}<input type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf" className="hidden" onChange={(e) => onChange(e.target.files?.[0])} /></label>
  </div>
);

const ServicesStep = ({ services, loading, form, patch, toggleService }) => (
  <div>
    <Heading title="Choose your services and rates" description="Select the services you offer and enter one hourly rate for each. This step is required." />
    <div className="mt-5">
      {loading ? <div className="py-10 text-center text-sm text-white/35">Loading services…</div> : (
        <div className="grid gap-2 lg:grid-cols-2">
          {services.map((service) => {
            const selected = form.selectedServices.includes(service._id);
            return (
              <div key={service._id} className={cn('grid min-w-0 grid-cols-[minmax(0,1fr)_110px] items-center gap-3 rounded-lg !cursor-pointer border px-3 py-2.5 sm:grid-cols-[minmax(0,1fr)_140px]', selected ? 'border-[#20c77a]/55 bg-[#0b2519]' : 'border-white/[0.08] bg-[#06110c]')}>
                <button type="button" onClick={() => toggleService(service)} className="flex min-w-0 items-center gap-2.5 border-0 bg-transparent p-0 text-left text-white">
                  <span className={cn('flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border border-solid cursor-pointer', selected ? 'border-[#20c77a] bg-[#20c77a] text-[#02140b]' : 'border-white/20')}>{selected && <Check size={11} strokeWidth={3}  />}</span>
                  <ServiceIcon className="cursor-pointer" serviceId={service.icon || service.slug} size={17} />
                  <span className="min-w-0 truncate text-xs font-semibold text-white/85 sm:text-sm cursor-pointer">{service.name}</span>
                </button>
                <div className="relative ">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-xs text-white/35">$</span>
                  <input className={`${inputClass} py-2.5 pl-7 pr-8 text-xs sm:text-sm`} type="number" min="0" step="1" disabled={!selected} value={form.rates[service._id] ?? ''} onChange={(e) => patch({ rates: { ...form.rates, [service._id]: e.target.value } })} placeholder="Rate" />
                  <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-[10px] text-white/25">/hr</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
      <p className="mb-0 mt-3 text-[11px] text-white/30">Rate fields are enabled only for selected services.</p>
    </div>
  </div>
);

const AvailabilityStep = ({ form, patch, schedulePreset, applyPreset, toggleDay, accountReady, servicesReady, verificationAdded, availabilityAdded }) => (
  <div>
    <Heading title="Set your availability" description="Choose a quick schedule or set custom days and times. You can skip this and set it later." optional />
    <div className="mt-5 grid gap-5 lg:grid-cols-[1.15fr_0.85fr]">
      <div className="space-y-4">
        <div>
          <span className={labelClass}>Quick schedule</span>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {[
              ['weekdays', 'Weekdays', 'Mon–Fri'],
              ['weekends', 'Weekends', 'Sat–Sun'],
              ['all', 'All days', 'Mon–Sun'],
              ['custom', 'Custom', 'Choose days'],
            ].map(([value, title, sub]) => (
              <button key={value} type="button" onClick={() => applyPreset(value)} className={cn('rounded-lg border px-2 py-3 text-center', schedulePreset === value ? 'border-[#20c77a] bg-[#0b2b1d] text-[#59e5a1]' : 'border-white/[0.09] bg-[#06110c] text-white/55')}>
                <span className="block text-xs font-semibold">{title}</span><span className="mt-1 block text-[9px] opacity-55">{sub}</span>
              </button>
            ))}
          </div>
        </div>
        <div className={panelClass}>
          <span className={labelClass}>Custom schedule</span>
          <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-7">
            {DAYS.map(([day]) => <button key={day} type="button" onClick={() => toggleDay(day)} className={cn('rounded-md border px-1 py-2 text-[10px] font-semibold', form.availableDays.includes(day) ? 'border-[#20c77a] bg-[#0d6d43] text-[#86ffc0]' : 'border-white/[0.08] bg-[#06110c] text-white/35')}>{day}</button>)}
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <Field label="From"><input className={inputClass} type="time" value={form.startTime} onChange={(e) => patch({ startTime: e.target.value, availabilityConfigured: true })} /></Field>
            <Field label="To"><input className={inputClass} type="time" value={form.endTime} onChange={(e) => patch({ endTime: e.target.value, availabilityConfigured: true })} /></Field>
          </div>
        </div>
        <button type="button" onClick={() => patch({ emergencyAvailable: !form.emergencyAvailable })} className="flex w-full items-start gap-3 rounded-lg border border-white/[0.08] bg-[#06110c] p-3.5 text-left">
          <span className={cn('mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-[3px] border border-solid', form.emergencyAvailable ? 'border-[#20c77a] bg-[#20c77a] text-[#02140b]' : 'border-white/20')}>{form.emergencyAvailable && <Check size={11} strokeWidth={3} />}</span>
          <span><span className="block text-xs font-semibold text-white/75">Available for urgent requests</span><span className="mt-1 block text-[10px] text-white/30">Optional preference. You can change it later.</span></span>
        </button>
      </div>

      <div className={`${panelClass} self-start`}>
        <h4 className="m-0 font-nunito text-lg font-bold">Ready to submit?</h4>
        <p className="mb-0 mt-1 text-xs text-white/35">Only Account and Services & Rates are required.</p>
        <div className="mt-4 space-y-3">
          <Ready complete={accountReady} label="Account details complete" required />
          <Ready complete={verificationAdded} label="Profile and verification added" />
          <Ready complete={servicesReady} label="Services and rates set" required />
          <Ready complete={availabilityAdded} label="Availability set" />
        </div>
        <div className="mt-5 rounded-lg border border-white/[0.07] bg-black/10 px-3 py-2.5 text-[10px] leading-4 text-white/30">Optional details can be completed later from Gigger Settings.</div>
      </div>
    </div>
  </div>
);

const Ready = ({ complete, label, required }) => (
  <div className="flex items-center gap-2.5 text-xs">
    <span className={cn('flex h-5 w-5 shrink-0 items-center justify-center rounded-full border', complete ? 'border-[#20c77a] bg-[#0d6d43] text-[#7dffba]' : 'border-white/12 bg-[#06110c] text-white/20')}>{complete ? <Check size={12} strokeWidth={3} /> : '•'}</span>
    <span className={complete ? 'text-white/70' : 'text-white/35'}>{label}</span>
    <span className="ml-auto text-[9px] uppercase tracking-wider text-white/20">{required ? 'Required' : 'Optional'}</span>
  </div>
);

export default ProviderRegister;